// =============================================================================
// VandieZ shipper-web -- API client
// =============================================================================
// Thin fetch wrapper that:
//   * Resolves the base URL from VITE_API_BASE_URL (Vite injects at build).
//   * Serializes / parses JSON.
//   * Translates non-2xx responses into `ApiError` carrying the
//     RFC 7807 problem-detail body.
//   * Accepts an optional access token for authenticated calls.
//
// No global auth header injection -- callers pass the token explicitly so
// SSR / server-component callers (none today, but possible later) can use
// the same module without depending on a React context for state.
// =============================================================================

import {
  ApiError,
  type OtpRequestPayload,
  type OtpRequestResponse,
  type OtpVerifyPayload,
  type OtpVerifyResponse,
  type ProblemDetail,
  type RefreshResponse,
  type UserRead,
} from "@/types/auth";
import type {
  BidAcceptResponse,
  BidsForLoadResponse,
  Load,
  LoadListResponse,
  LoadPatchPayload,
  LoadPostPayload,
  PublicPricingModel,
  LoadCancellationPreview,
  ReferenceItem,
  RoutePreviewResponse,
  RoutePreviewStopPayload,
} from "@/types/load";
import type {
  GpsHistoryResponse,
  Trip,
  TripListResponse,
} from "@/types/trip";
import type {
  Vehicle,
  VehicleListResponse,
  VehiclePatchPayload,
} from "@/types/vehicle";
import type {
  DisputeListResponse,
  DisputeRaisePayload,
  DisputeRead,
} from "@/types/dispute";
import type {
  TicketCreatePayload,
  TicketListResponse,
  TicketReplyPayload,
  TicketWithMessages,
} from "@/types/support";
import type {
  BankAccountCreatePayload,
  BankAccountListResponse,
  BankAccountRead,
  KycDocumentType,
  KycStatusResponse,
  KycSubmitResponse,
  KycUploadResponse,
  UserMeResponse,
  UserUpdateShipperPayload,
} from "@/types/user";
import type { WalletSummary } from "@/types/wallet";

const BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL) ??
  "http://localhost:8000/api/v1";

interface FetchOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE" | "PUT";
  body?: unknown;
  accessToken?: string;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  const { method = "GET", body, accessToken, signal } = opts;

  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  const fetchOptions: RequestInit = {
    method,
    headers,
    credentials: "omit",
  };
  if (body !== undefined) {
    fetchOptions.body = JSON.stringify(body);
  }
  if (signal !== undefined) {
    fetchOptions.signal = signal;
  }

  const response = await fetch(`${BASE_URL}${path}`, fetchOptions);

  if (response.status === 204) {
    return undefined as unknown as T;
  }

  const text = await response.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = {
        status: response.status,
        title: response.statusText,
        detail: text.slice(0, 500),
      };
    }
  }

  if (!response.ok) {
    const problem: ProblemDetail =
      parsed !== null &&
      typeof parsed === "object" &&
      "status" in (parsed)
        ? (parsed as ProblemDetail)
        : {
            status: response.status,
            title: response.statusText,
            detail: `Request failed with status ${response.status}`,
          };
    throw new ApiError(problem);
  }

  return parsed as T;
}

// =============================================================================
// Auth endpoint wrappers
// =============================================================================
export const authApi = {
  requestOtp(payload: OtpRequestPayload, signal?: AbortSignal): Promise<OtpRequestResponse> {
    return request<OtpRequestResponse>("/auth/otp/request", {
      method: "POST",
      body: payload,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  verifyOtp(payload: OtpVerifyPayload, signal?: AbortSignal): Promise<OtpVerifyResponse> {
    return request<OtpVerifyResponse>("/auth/otp/verify", {
      method: "POST",
      body: payload,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  refresh(refreshToken: string, signal?: AbortSignal): Promise<RefreshResponse> {
    return request<RefreshResponse>("/auth/refresh", {
      method: "POST",
      body: { refresh_token: refreshToken },
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  async logout(accessToken: string, refreshToken?: string): Promise<void> {
    await request<undefined>("/auth/logout", {
      method: "POST",
      accessToken,
      body: refreshToken ? { refresh_token: refreshToken } : undefined,
    });
  },

  me(accessToken: string, signal?: AbortSignal): Promise<UserRead> {
    return request<UserRead>("/auth/me", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },
};

// =============================================================================
// Loads endpoints (shipper-facing MVP)
// =============================================================================
// Source: backend/app/api/v1/loads.py
//   POST   /loads        -- post a new load
//   GET    /loads        -- list shipper's own loads
//   GET    /loads/{id}   -- detail
//   DELETE /loads/{id}   -- cancel
/**
 * The pricing models a load may currently be posted under (p9).
 *
 * Enabled-only, straight from the catalog -- so enabling `fixed` in the
 * admin screen makes it appear here without a client release, which is the
 * whole point of keeping the catalog in a table (D-2).
 */
export const pricingModelsApi = {
  listEnabled(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<PublicPricingModel[]> {
    return request<PublicPricingModel[]>("/platform/pricing-models", {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },
};

/**
 * Business master catalogs (C-24). Enabled-only and authed, any role.
 *
 * The whole reason this exists is that curating a goods type in the admin
 * screen must reach the post-load form with no client release. A hardcoded
 * list here would make the catalog decorative.
 */
/** PR-07 / p126. Advisory stop ordering while the shipper is still typing. */
export const routeApi = {
  preview(
    accessToken: string,
    stops: RoutePreviewStopPayload[],
  ): Promise<RoutePreviewResponse> {
    return request<RoutePreviewResponse>("/loads/route-preview", {
      method: "POST",
      accessToken,
      body: { stops },
    });
  },
};

export const referenceDataApi = {
  listGoodsTypes(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<ReferenceItem[]> {
    return request<ReferenceItem[]>("/reference-data?category=goods_type", {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },
};

export const loadsApi = {
  list(
    accessToken: string,
    params: { limit?: number; offset?: number; status?: string } = {},
    signal?: AbortSignal,
  ): Promise<LoadListResponse> {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) {qs.set("limit", String(params.limit));}
    if (params.offset !== undefined) {qs.set("offset", String(params.offset));}
    if (params.status !== undefined) {qs.set("status", params.status);}
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<LoadListResponse>(`/loads${suffix}`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  detail(accessToken: string, loadId: string, signal?: AbortSignal): Promise<Load> {
    return request<Load>(`/loads/${loadId}`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  post(accessToken: string, payload: LoadPostPayload): Promise<Load> {
    return request<Load>("/loads", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  patch(
    accessToken: string,
    loadId: string,
    payload: LoadPatchPayload,
  ): Promise<Load> {
    return request<Load>(`/loads/${loadId}`, {
      method: "PATCH",
      accessToken,
      body: payload,
    });
  },

  /** p130: what cancelling would cost, BEFORE cancelling. Computed by
   *  the same resolver that bills the cancel, so the quote is the bill. */
  cancellationPreview(
    accessToken: string,
    loadId: string,
    signal?: AbortSignal,
  ): Promise<LoadCancellationPreview> {
    return request<LoadCancellationPreview>(
      `/loads/${loadId}/cancellation-preview`,
      { accessToken, ...(signal !== undefined ? { signal } : {}) },
    );
  },

  cancel(accessToken: string, loadId: string, reason?: string): Promise<unknown> {
    return request<unknown>(`/loads/${loadId}`, {
      method: "DELETE",
      accessToken,
      body: reason ? { cancellation_reason: reason } : undefined,
    });
  },

  bids(
    accessToken: string,
    loadId: string,
    signal?: AbortSignal,
  ): Promise<BidsForLoadResponse> {
    return request<BidsForLoadResponse>(`/loads/${loadId}/bids`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },
};

// =============================================================================
// Bids endpoints (shipper-facing accept/reject)
// =============================================================================
// Source: backend/app/api/v1/bids.py
//   POST /bids/{id}/accept   -- lock load, create trip, reveal PII
//   POST /bids/{id}/reject   -- explicit shipper reject
export const bidsApi = {
  accept(
    accessToken: string,
    bidId: string,
    paymentMethod: "cash" | "online",
  ): Promise<BidAcceptResponse> {
    return request<BidAcceptResponse>(`/bids/${bidId}/accept`, {
      method: "POST",
      accessToken,
      body: { payment_method: paymentMethod },
    });
  },

  reject(accessToken: string, bidId: string, reason?: string): Promise<unknown> {
    return request<unknown>(`/bids/${bidId}/reject`, {
      method: "POST",
      accessToken,
      body: reason ? { rejection_reason: reason } : undefined,
    });
  },
};

// =============================================================================
// Trips endpoints (shipper-facing tracking)
// =============================================================================
//   GET /trips                   -- list caller-scoped trips
//   GET /trips/{id}              -- trip detail
//   GET /gps/{trip_id}/gps       -- recent location pings (history)
export const tripsApi = {
  list(
    accessToken: string,
    params: { limit?: number; offset?: number; status?: string } = {},
    signal?: AbortSignal,
  ): Promise<TripListResponse> {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) {qs.set("limit", String(params.limit));}
    if (params.offset !== undefined) {qs.set("offset", String(params.offset));}
    if (params.status !== undefined) {qs.set("status", params.status);}
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<TripListResponse>(`/trips${suffix}`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  detail(accessToken: string, tripId: string, signal?: AbortSignal): Promise<Trip> {
    return request<Trip>(`/trips/${tripId}`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  gpsHistory(
    accessToken: string,
    tripId: string,
    signal?: AbortSignal,
  ): Promise<GpsHistoryResponse> {
    return request<GpsHistoryResponse>(`/gps/${tripId}/gps`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },
};

// =============================================================================
// Vehicles (caller's own fleet)
// =============================================================================
// Source: backend/app/api/v1/vehicles.py
//   GET    /vehicles                   -- list caller's vehicles
//   GET    /vehicles/{id}              -- detail
//   PATCH  /vehicles/{id}              -- update editable fields
//   POST   /vehicles/{id}/toggle-online -- flip online_for_bidding
//
// Onboarding (POST /vehicles) is intentionally omitted: it requires 5 KYC
// document IDs + 4+ photo IDs uploaded via the KYC pipeline, which needs
// the camera/file picker on mobile. Web users are pointed at the app.
export const vehiclesApi = {
  list(
    accessToken: string,
    params: { limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<VehicleListResponse> {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) {qs.set("limit", String(params.limit));}
    if (params.offset !== undefined) {qs.set("offset", String(params.offset));}
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<VehicleListResponse>(`/vehicles${suffix}`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  detail(accessToken: string, vehicleId: string, signal?: AbortSignal): Promise<Vehicle> {
    return request<Vehicle>(`/vehicles/${vehicleId}`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  patch(
    accessToken: string,
    vehicleId: string,
    payload: VehiclePatchPayload,
  ): Promise<Vehicle> {
    return request<Vehicle>(`/vehicles/${vehicleId}`, {
      method: "PATCH",
      accessToken,
      body: payload,
    });
  },

  toggleOnline(accessToken: string, vehicleId: string): Promise<Vehicle> {
    return request<Vehicle>(`/vehicles/${vehicleId}/toggle-online`, {
      method: "POST",
      accessToken,
    });
  },
};

// =============================================================================
// Wallet (caller's own)
// =============================================================================
//   GET /users/me/wallet -- balance + recent ledger entries
export interface WalletRechargeResponse {
  recharge_id: string;
  qr_image_url: string | null;
  amount: string;
  expires_at: string;
}

export interface WalletRechargeStatusResponse {
  recharge_id: string;
  status: "created" | "paid" | "expired" | "failed";
  amount: string;
  paid_at: string | null;
}

export const walletApi = {
  me(accessToken: string, signal?: AbortSignal): Promise<WalletSummary> {
    return request<WalletSummary>("/users/me/wallet", {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  recharge(
    accessToken: string,
    amount: number,
  ): Promise<WalletRechargeResponse> {
    return request<WalletRechargeResponse>("/users/me/wallet/recharge", {
      method: "POST",
      accessToken,
      body: { amount: String(amount) },
    });
  },

  rechargeStatus(
    accessToken: string,
    rechargeId: string,
    signal?: AbortSignal,
  ): Promise<WalletRechargeStatusResponse> {
    return request<WalletRechargeStatusResponse>(
      `/users/me/wallet/recharge/${rechargeId}`,
      {
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },
};

// =============================================================================
// Profile (GET / PATCH /users/me)
// =============================================================================
// Source: backend/app/api/v1/users.py
//   GET   /users/me  -- full profile (identity + nested KYC + bank accounts)
//   PATCH /users/me  -- update editable fields (role-specific subset)
export const profileApi = {
  me(accessToken: string, signal?: AbortSignal): Promise<UserMeResponse> {
    return request<UserMeResponse>("/users/me", {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  update(
    accessToken: string,
    payload: UserUpdateShipperPayload,
  ): Promise<UserMeResponse> {
    return request<UserMeResponse>("/users/me", {
      method: "PATCH",
      accessToken,
      body: payload,
    });
  },
};

// =============================================================================
// Bank accounts (/users/me/bank-accounts)
// =============================================================================
// Source: backend/app/api/v1/bank_accounts.py
//   GET    /users/me/bank-accounts                   -- list mine
//   POST   /users/me/bank-accounts                   -- add new
//   POST   /users/me/bank-accounts/{id}/set-primary  -- flip primary
//   DELETE /users/me/bank-accounts/{id}              -- soft delete
export const bankAccountsApi = {
  list(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<BankAccountListResponse> {
    return request<BankAccountListResponse>("/users/me/bank-accounts", {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  create(
    accessToken: string,
    payload: BankAccountCreatePayload,
  ): Promise<BankAccountRead> {
    return request<BankAccountRead>("/users/me/bank-accounts", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  setPrimary(
    accessToken: string,
    bankAccountId: string,
  ): Promise<BankAccountRead> {
    return request<BankAccountRead>(
      `/users/me/bank-accounts/${bankAccountId}/set-primary`,
      {
        method: "POST",
        accessToken,
        body: {},
      },
    );
  },

  remove(accessToken: string, bankAccountId: string): Promise<unknown> {
    return request<unknown>(`/users/me/bank-accounts/${bankAccountId}`, {
      method: "DELETE",
      accessToken,
    });
  },
};

// =============================================================================
// KYC (/api/v1/kyc/*)
// =============================================================================
// Source: backend/app/api/v1/kyc.py + uploads.py
//   POST /uploads/kyc-document     -- multipart upload, returns storage_key
//   POST /kyc/documents            -- pin storage_key to a KYC row
//   POST /kyc/submit               -- submit for admin review
//   GET  /kyc/status               -- overall + per-doc + actions
export const kycApi = {
  status(accessToken: string, signal?: AbortSignal): Promise<KycStatusResponse> {
    return request<KycStatusResponse>("/kyc/status", {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  /**
   * Multipart upload of a KYC file (image or PDF). Returns the opaque
   * storage_key the caller must then pass to `kycApi.createDocument`.
   */
  async uploadFile(
    accessToken: string,
    file: File,
  ): Promise<KycUploadResponse> {
    const form = new FormData();
    form.append("file", file);

    const response = await fetch(`${BASE_URL}/uploads/kyc-document`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: form,
    });

    const text = await response.text();
    let parsed: unknown = null;
    if (text.length > 0) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = {
          status: response.status,
          title: response.statusText,
          detail: text.slice(0, 500),
        };
      }
    }
    if (!response.ok) {
      const problem: ProblemDetail =
        parsed !== null &&
        typeof parsed === "object" &&
        "status" in parsed
          ? (parsed as ProblemDetail)
          : {
              status: response.status,
              title: response.statusText,
              detail: `Upload failed with status ${response.status}`,
            };
      throw new ApiError(problem);
    }
    return parsed as KycUploadResponse;
  },

  createDocument(
    accessToken: string,
    payload: { storage_key: string; document_type: KycDocumentType },
  ): Promise<unknown> {
    return request<unknown>("/kyc/documents", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  submit(accessToken: string): Promise<KycSubmitResponse> {
    return request<KycSubmitResponse>("/kyc/submit", {
      method: "POST",
      accessToken,
    });
  },
};

// =============================================================================
// Support tickets (/users/me/tickets)
// =============================================================================
// Source: backend/app/api/v1/support.py
//   POST  /users/me/tickets                       -- create + first message
//   GET   /users/me/tickets                       -- list mine
//   GET   /users/me/tickets/{id}                  -- detail + thread
//   POST  /users/me/tickets/{id}/messages         -- append reply
//   POST  /users/me/tickets/{id}/close            -- user-closes
export const supportApi = {
  list(
    accessToken: string,
    params: { limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<TicketListResponse> {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) {qs.set("limit", String(params.limit));}
    if (params.offset !== undefined) {qs.set("offset", String(params.offset));}
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<TicketListResponse>(`/users/me/tickets${suffix}`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  detail(
    accessToken: string,
    ticketId: string,
    signal?: AbortSignal,
  ): Promise<TicketWithMessages> {
    return request<TicketWithMessages>(`/users/me/tickets/${ticketId}`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  create(
    accessToken: string,
    payload: TicketCreatePayload,
  ): Promise<TicketWithMessages> {
    return request<TicketWithMessages>("/users/me/tickets", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  reply(
    accessToken: string,
    ticketId: string,
    payload: TicketReplyPayload,
  ): Promise<TicketWithMessages> {
    return request<TicketWithMessages>(
      `/users/me/tickets/${ticketId}/messages`,
      {
        method: "POST",
        accessToken,
        body: payload,
      },
    );
  },

  close(accessToken: string, ticketId: string): Promise<TicketWithMessages> {
    return request<TicketWithMessages>(
      `/users/me/tickets/${ticketId}/close`,
      {
        method: "POST",
        accessToken,
      },
    );
  },
};

// =============================================================================
// Disputes (/trips/{trip_id}/disputes)
// =============================================================================
// Source: backend/app/api/v1/disputes.py
//   POST /trips/{trip_id}/disputes   -- raise
//   GET  /trips/{trip_id}/disputes   -- list disputes on this trip
//
// Note: the backend exposes disputes scoped to a trip (no `list mine`
// endpoint). The shipper-web disputes page therefore wires through the
// list of completed trips and shows disputes per trip; the raise flow
// also starts from a trip.
export const disputesApi = {
  /** Flat feed of every dispute on the caller's trips (newest first).
   * Backed by GET /users/me/disputes. Filters: status, limit, offset. */
  listMine(
    accessToken: string,
    params: {
      status?: "open" | "under_review" | "resolved" | "rejected";
      limit?: number;
      offset?: number;
    } = {},
    signal?: AbortSignal,
  ): Promise<DisputeListResponse> {
    const qs = new URLSearchParams();
    if (params.status !== undefined) {qs.set("status", params.status);}
    if (params.limit !== undefined) {qs.set("limit", String(params.limit));}
    if (params.offset !== undefined) {qs.set("offset", String(params.offset));}
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<DisputeListResponse>(`/users/me/disputes${suffix}`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  listForTrip(
    accessToken: string,
    tripId: string,
    signal?: AbortSignal,
  ): Promise<DisputeListResponse> {
    return request<DisputeListResponse>(`/trips/${tripId}/disputes`, {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  raise(
    accessToken: string,
    tripId: string,
    payload: DisputeRaisePayload,
  ): Promise<DisputeRead> {
    return request<DisputeRead>(`/trips/${tripId}/disputes`, {
      method: "POST",
      accessToken,
      body: payload,
    });
  },
};

// =============================================================================
// Ratings (caller-received)
// =============================================================================
// Source: backend/app/api/v1/users.py
//   GET /users/me/ratings — summary + items received by the caller
import type { RatingsResponse } from "@/types/ratings";

export const ratingsApi = {
  me(accessToken: string, signal?: AbortSignal): Promise<RatingsResponse> {
    return request<RatingsResponse>("/users/me/ratings", {
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },
};

// =============================================================================
// In-app notifications inbox
// =============================================================================
// Source: backend/app/api/v1/notifications.py
//   GET  /users/me/notifications              — paginated feed + counts
//   POST /users/me/notifications/{id}/read    — flip read_at, idempotent
//   POST /users/me/notifications/read-all     — bulk flip, returns count
import type {
  NotificationListResponse,
  NotificationReadAllResponse,
  NotificationReadResponse,
} from "@/types/inbox";

export const inboxApi = {
  list(
    accessToken: string,
    params: { only_unread?: boolean; limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<NotificationListResponse> {
    const qs = new URLSearchParams();
    if (params.only_unread === true) {qs.set("only_unread", "true");}
    if (params.limit !== undefined) {qs.set("limit", String(params.limit));}
    if (params.offset !== undefined) {qs.set("offset", String(params.offset));}
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<NotificationListResponse>(
      `/users/me/notifications${suffix}`,
      {
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  markOneRead(
    accessToken: string,
    notificationId: string,
  ): Promise<NotificationReadResponse> {
    return request<NotificationReadResponse>(
      `/users/me/notifications/${notificationId}/read`,
      {
        method: "POST",
        accessToken,
      },
    );
  },

  markAllRead(
    accessToken: string,
  ): Promise<NotificationReadAllResponse> {
    return request<NotificationReadAllResponse>(
      "/users/me/notifications/read-all",
      {
        method: "POST",
        accessToken,
      },
    );
  },
};

// =============================================================================
// Re-exports
// =============================================================================
// Exposed so the file-serve endpoint URL (auth-gated; serves KYC + ticket
// attachment + dispute evidence bytes) can be referenced from page-level
// code that needs to embed an authenticated <img src/>. The current pages
// keep file-serving in-band so don't import this directly; left as an
// escape hatch.
export { BASE_URL };

// =============================================================================
// CU-18 address book + CU-27 postponement + PR-11 estimate
// =============================================================================
export interface SavedAddress {
  id: string;
  label: string;
  address: string;
  district: string;
  lat: string;
  lng: string;
  contact_name: string | null;
  contact_phone: string | null;
  is_default: boolean;
  created_at: string;
}

export interface SavedAddressListResponse {
  items: SavedAddress[];
  total: number;
}

export interface SavedAddressPayload {
  label: string;
  address: string;
  district: string;
  lat: string;
  lng: string;
  contact_name?: string | null;
  contact_phone?: string | null;
  is_default?: boolean;
}

export const addressesApi = {
  list(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<SavedAddressListResponse> {
    return request<SavedAddressListResponse>("/users/me/addresses", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  create(
    accessToken: string,
    payload: SavedAddressPayload,
  ): Promise<SavedAddress> {
    return request<SavedAddress>("/users/me/addresses", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  /** Promote to default. The server clears the previous one in the same
   *  transaction -- a partial unique index means two defaults is a 500,
   *  not a shrug. */
  makeDefault(accessToken: string, addressId: string): Promise<SavedAddress> {
    return request<SavedAddress>(`/users/me/addresses/${addressId}`, {
      method: "PUT",
      accessToken,
      body: { is_default: true },
    });
  },

  remove(accessToken: string, addressId: string): Promise<void> {
    return request<void>(`/users/me/addresses/${addressId}`, {
      method: "DELETE",
      accessToken,
    });
  },
};

export interface PostponeResponse {
  load_id: string;
  status: string;
  postpone_count: number;
  postpones_remaining: number;
  pickup_window_start: string;
  pickup_window_end: string;
  original_pickup_window_start: string | null;
  notified_bidder_count: number;
}

export const postponementApi = {
  /** CU-27: keeps the load and its bids. Cancel+repost loses both. */
  postpone(
    accessToken: string,
    loadId: string,
    body: {
      new_pickup_window_start: string;
      new_pickup_window_end: string;
      reason: string;
    },
  ): Promise<PostponeResponse> {
    return request<PostponeResponse>(`/loads/${loadId}/postpone`, {
      method: "POST",
      accessToken,
      body,
    });
  },

  resume(accessToken: string, loadId: string): Promise<PostponeResponse> {
    return request<PostponeResponse>(`/loads/${loadId}/resume`, {
      method: "POST",
      accessToken,
    });
  },
};

export interface QuoteComponent {
  label: string;
  amount_inr: string;
  detail: string | null;
}

export interface LoadQuote {
  pricing_model: string;
  total_inr: string;
  components: QuoteComponent[];
  minimum_applied: boolean;
  notes: string[];
  fare_rule_id: string | null;
}

export const quoteApi = {
  /** PR-11: prices a load BEFORE posting. Creates nothing, so it is safe
   *  to call on every keystroke-debounced change. */
  estimate(
    accessToken: string,
    body: Record<string, unknown>,
  ): Promise<LoadQuote> {
    return request<LoadQuote>("/loads/quote-estimate", {
      method: "POST",
      accessToken,
      body,
    });
  },
};
